import React, {useEffect, useMemo, useState} from 'react';
import {Button, Card, Checkbox, Empty, Spin, message} from 'antd';
import {useQueryClient} from '@tanstack/react-query';
import {useAllRole, useDirectoryRoles} from '../../hooks/useRoleBinding';
import {roleService} from '../../services/role';
import './index.scss';

interface RolePermissionPanelProps {
  node: any;
}

export function RolePermissionPanel({node}: RolePermissionPanelProps) {
  const directoryId = node?.id;

  const queryClient = useQueryClient();
  const [checkedRoleIds, setCheckedRoleIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const {data: rolesData, isLoading: rolesLoading} = useAllRole();
  const {data: directoryRolesData, isLoading: bindingLoading} = useDirectoryRoles(directoryId);

  const roles = useMemo(() => {
    return Array.isArray(rolesData) ? rolesData : rolesData?.data || [];
  }, [rolesData]);

  const directoryRoles = useMemo(() => {
    return Array.isArray(directoryRolesData)
      ? directoryRolesData
      : directoryRolesData?.data || [];
  }, [directoryRolesData]);

  useEffect(() => {
    if (!directoryId) {
      setCheckedRoleIds([]);
      return;
    }

    setCheckedRoleIds(
      directoryRoles.map((item: any) => String(item.role_id))
    );
  }, [directoryId, directoryRoles]);

  async function handleSave() {
    if (!directoryId) return;

    //TODO 未来应该改成批量操作
    const oldMap = new Map(
      directoryRoles.map((item: any) => [String(item.role_id), item.id])
    );

    const oldIds = directoryRoles.map((item: any) => String(item.role_id));
    const nextIds = checkedRoleIds;

    const addIds = nextIds.filter(id => !oldIds.includes(id));
    const deleteIds = oldIds.filter((id: string) => !nextIds.includes(id));

    setSaving(true);

    try {
      await Promise.all([
        ...addIds.map(roleId => roleService.createRel({
          role_id: roleId,
          directory_id: directoryId
        })),

        ...deleteIds.map((roleId: any) => {
          const rel: any = oldMap.get(roleId);
          return roleService.deleteRel({
            id: rel
          });
        })
      ]);

      await queryClient.invalidateQueries({
        queryKey: ['directory-roles', directoryId]
      });

      void message.success('保存成功');
    } catch (e) {
      void message.error('保存失败');
    } finally {
      setSaving(false);
    }
  }

  if (!node) {
    return (
      <Card className="drive-role-panel">
        <Empty description="请选择左侧组织或部门"/>
      </Card>
    );
  }

  const loading = rolesLoading || bindingLoading;

  return (
    <Card className="drive-role-panel">
      <div className="drive-role-panel__header">
        <div>
          <h3>{node.name}</h3>
          <p>配置哪些角色可以看到该组织目录</p>
        </div>

        <Button
          type="primary"
          loading={saving}
          disabled={loading}
          onClick={handleSave}
        >
          保存
        </Button>
      </div>

      {loading ? (
        <div className="drive-role-panel__loading">
          <Spin/>
        </div>
      ) : (
        <Checkbox.Group
          value={checkedRoleIds}
          onChange={values => setCheckedRoleIds(values.map(String))}
          className="drive-role-panel__group"
        >
          {roles.map((role: any) => {
            const roleId = String(role.id || role.role_id);

            return (
              <Checkbox
                key={roleId}
                value={roleId}
                className="drive-role-panel__name"
              >
                {role.name || role.role_name}
              </Checkbox>
            );
          })}
        </Checkbox.Group>
      )}
    </Card>
  );
}